import { useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Calendar, Crown, ChevronRight, Globe, Clock, Tv, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { WORLD_CHAMPIONSHIP_2026 } from "@/data/internationalTournaments";

interface UpcomingEventsSpotlightProps {
  showUpgradePrompt: boolean;
  onUpgrade: () => void;
}

// Country flag mapping for WC teams
const FLAGS: Record<string, string> = {
  FIN: "🇫🇮", CZE: "🇨🇿", CAN: "🇨🇦", USA: "🇺🇸", SUI: "🇨🇭", SWE: "🇸🇪",
  NOR: "🇳🇴", LAT: "🇱🇻", DEN: "🇩🇰", GER: "🇩🇪", SVK: "🇸🇰", AUT: "🇦🇹",
  SLO: "🇸🇮", ITA: "🇮🇹", FRA: "🇫🇷", KAZ: "🇰🇿", HUN: "🇭🇺", TBD: "🏒",
};

// Worlds broadcast rights (IIHF Worlds 2026)
const WORLDS_BROADCASTS: { country: string; flag: string; channels: { name: string; url?: string }[] }[] = [
  { country: "Sweden", flag: "🇸🇪", channels: [
    { name: "TV4 / TV4 Play", url: "https://www.tv4play.se" },
    { name: "C More", url: "https://www.cmore.se" },
  ]},
  { country: "Finland", flag: "🇫🇮", channels: [
    { name: "MTV3 / Katsomo", url: "https://www.mtv.fi" },
    { name: "C More", url: "https://www.cmore.fi" },
  ]},
  { country: "USA", flag: "🇺🇸", channels: [
    { name: "NHL Network", url: "https://www.nhl.com/nhlnetwork" },
    { name: "ESPN+", url: "https://plus.espn.com" },
  ]},
  { country: "Canada", flag: "🇨🇦", channels: [
    { name: "TSN", url: "https://www.tsn.ca" },
    { name: "RDS", url: "https://www.rds.ca" },
  ]},
  { country: "Czechia", flag: "🇨🇿", channels: [
    { name: "ČT Sport", url: "https://www.ceskatelevize.cz/sport" },
  ]},
  { country: "Germany", flag: "🇩🇪", channels: [
    { name: "ProSieben MAXX", url: "https://www.prosiebenmaxx.de" },
    { name: "Sport1", url: "https://www.sport1.de" },
  ]},
  { country: "Switzerland", flag: "🇨🇭", channels: [
    { name: "SRF", url: "https://www.srf.ch/sport" },
    { name: "RSI", url: "https://www.rsi.ch/sport" },
  ]},
  { country: "Europe (general)", flag: "🇪🇺", channels: [
    { name: "Viaplay", url: "https://viaplay.com" },
  ]},
];

export default function UpcomingEventsSpotlight({ showUpgradePrompt, onUpgrade }: UpcomingEventsSpotlightProps) {
  const [expandedGame, setExpandedGame] = useState<string | null>(null);

  // Pull upcoming knockout games from WC 2026
  const upcomingGames = WORLD_CHAMPIONSHIP_2026.knockoutRounds
    ?.flatMap(round => round.matchups.map(m => ({ ...m, roundName: round.name })))
    .filter(m => m.status === "upcoming")
    .slice(0, 3) ?? [];

  return (
    <section className="mb-10">
      {/* Upgrade Teaser for Free Users */}
      {showUpgradePrompt && (
        <Card className="mb-6 border-amber-500/30 bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-500/20 rounded-xl">
                  <Globe className="w-8 h-8 text-amber-400" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold text-foreground flex items-center gap-2">
                    Choose Your Favorite Nation
                    <Badge variant="outline" className="text-xs border-amber-500/50 text-amber-400">Pro/Elite</Badge>
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Get a personalized dashboard with your country's games, players, and alerts.
                  </p>
                </div>
              </div>
              <Button onClick={onUpgrade} className="gap-2 bg-amber-500 hover:bg-amber-600 text-black font-semibold">
                <Crown className="w-4 h-4" />
                Set Favorite Nation
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* WC Spotlight Card */}
      <Card className="relative overflow-hidden border-primary/30 bg-gradient-to-br from-primary/10 via-transparent to-transparent">
        <div className="absolute top-0 right-0 w-48 h-48 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />

        <CardContent className="relative p-6 md:p-8">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-4">
                <Trophy className="w-6 h-6 text-primary" />
                <Badge variant="outline" className="text-xs border-emerald-500/50 text-emerald-400">Completed</Badge>
              </div>

              <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-2">
                IIHF World Championship 2026
              </h2>
              <p className="text-muted-foreground mb-2">
                {WORLD_CHAMPIONSHIP_2026.dates} · {WORLD_CHAMPIONSHIP_2026.host}
              </p>
              {WORLD_CHAMPIONSHIP_2026.medals && (
                <p className="text-sm text-foreground mb-4">
                  🥇 {FLAGS[WORLD_CHAMPIONSHIP_2026.medals.gold]} {WORLD_CHAMPIONSHIP_2026.medals.gold}
                  {"  ·  "}
                  🥈 {FLAGS[WORLD_CHAMPIONSHIP_2026.medals.silver]} {WORLD_CHAMPIONSHIP_2026.medals.silver}
                  {"  ·  "}
                  🥉 {FLAGS[WORLD_CHAMPIONSHIP_2026.medals.bronze]} {WORLD_CHAMPIONSHIP_2026.medals.bronze}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-3">
                <Link to="/#international-tournaments">
                  <Button className="gap-2">
                    <Trophy className="w-4 h-4" />
                    View Bracket
                  </Button>
                </Link>
                <Link to="/#big-events">
                  <Button variant="outline" className="gap-2">
                    <Calendar className="w-4 h-4" />
                    All Upcoming Events
                  </Button>
                </Link>
              </div>
            </div>

            {/* Next Puck Drops with broadcast info */}
            <div className="lg:w-80">
              <h3 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3 flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5 text-primary" />
                {upcomingGames.length > 0 ? "Next Puck Drops" : "Final Results"}
              </h3>
              <div className="space-y-2">
                {upcomingGames.length === 0 && WORLD_CHAMPIONSHIP_2026.medals && (
                  <div className="bg-secondary/50 rounded-lg p-3 space-y-1.5">
                    <div className="flex items-center gap-2 text-sm">
                      <span>🥇</span>
                      <span>{FLAGS[WORLD_CHAMPIONSHIP_2026.medals.gold] ?? "🏒"}</span>
                      <span className="text-foreground font-semibold">{WORLD_CHAMPIONSHIP_2026.medals.gold}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span>🥈</span>
                      <span>{FLAGS[WORLD_CHAMPIONSHIP_2026.medals.silver] ?? "🏒"}</span>
                      <span className="text-foreground font-medium">{WORLD_CHAMPIONSHIP_2026.medals.silver}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <span>🥉</span>
                      <span>{FLAGS[WORLD_CHAMPIONSHIP_2026.medals.bronze] ?? "🏒"}</span>
                      <span className="text-foreground font-medium">{WORLD_CHAMPIONSHIP_2026.medals.bronze}</span>
                    </div>
                  </div>
                )}
                {upcomingGames.map((game) => {
                  const isExpanded = expandedGame === game.id;
                  return (
                    <div key={game.id} className="bg-secondary/50 rounded-lg p-3 transition-colors">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] text-muted-foreground">
                          {game.roundName} · {game.date}
                        </span>
                        <span className="text-xs font-bold text-primary">{game.time}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <span>{FLAGS[game.team1] ?? "🏒"}</span>
                        <span className="text-foreground font-medium">{game.team1}</span>
                        <span className="text-muted-foreground">vs</span>
                        <span className="text-foreground font-medium">{game.team2}</span>
                        <span>{FLAGS[game.team2] ?? "🏒"}</span>
                      </div>
                      {game.venue && (
                        <p className="text-[10px] text-muted-foreground mt-1">{game.venue}</p>
                      )}
                      <button
                        onClick={() => setExpandedGame(isExpanded ? null : game.id)}
                        className="flex items-center gap-1 mt-1.5 text-[10px] text-primary hover:text-primary/80 transition-colors w-full"
                      >
                        <Tv className="h-3 w-3" />
                        <span className="font-medium">Where to Watch</span>
                        {isExpanded ? <ChevronUp className="h-3 w-3 ml-auto" /> : <ChevronDown className="h-3 w-3 ml-auto" />}
                      </button>
                      {isExpanded && (
                        <div className="mt-2 space-y-1 border-t border-border/30 pt-2">
                          {WORLDS_BROADCASTS.map((b) => (
                            <div key={b.country} className="flex items-center gap-1.5 text-[11px]">
                              <span>{b.flag}</span>
                              <span className="text-muted-foreground">
                                {b.channels.map((ch, i) => (
                                  <span key={ch.name}>
                                    {i > 0 && " · "}
                                    {ch.url ? (
                                      <a href={ch.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                                        {ch.name}
                                      </a>
                                    ) : ch.name}
                                  </span>
                                ))}
                              </span>
                            </div>
                          ))}
                          <p className="text-[10px] text-muted-foreground/70 pt-1 italic">
                            Channel assignments may vary by game — check your local broadcaster.
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Links */}
      <div className="mt-4 flex flex-wrap gap-2">
        <Link to="/#international-tournaments">
          <Badge variant="secondary" className="gap-1 hover:bg-secondary/80 cursor-pointer">
            <Trophy className="w-3 h-3" />
            World Championship
            <ChevronRight className="w-3 h-3" />
          </Badge>
        </Link>
        <Link to="/#live-games">
          <Badge variant="secondary" className="gap-1 hover:bg-secondary/80 cursor-pointer">
            Live Games
            <ChevronRight className="w-3 h-3" />
          </Badge>
        </Link>
        <Link to="/schedule">
          <Badge variant="secondary" className="gap-1 hover:bg-secondary/80 cursor-pointer">
            <Calendar className="w-3 h-3" />
            Full Schedule
            <ChevronRight className="w-3 h-3" />
          </Badge>
        </Link>
      </div>
    </section>
  );
}
